import { api } from "@/lib/api";

const base = "/api/pharmacy";

export type PharmacyPurchaseOrderStatus =
  | "DRAFT"
  | "SENT"
  | "VENDOR_SUBMITTED"
  | "PARTIALLY_ACCEPTED"
  | "ACCEPTED"
  | "REJECTED"
  | "CANCELLED";

export interface PurchaseOrderLine {
  id: string;
  drugId: string;
  orderedQty: number;
  notes?: string | null;
  drug?: {
    id: string;
    name: string;
    strength?: string | null;
    form?: string | null;
    unit?: string | null;
    gstRate?: number | null;
  };
}

export interface VendorDeliveryLine {
  id: string;
  drugId: string;
  batchNo: string;
  expiryDate: string;
  quantity: number;
  mrp: number;
  purchaseRate: number;
  gstRate?: number | null;
  discountRate?: number | null;
  notes?: string | null;
  stockLedgerId?: string | null;
  drug?: PurchaseOrderLine["drug"];
}

export interface PharmacyPurchaseOrder {
  id: string;
  orderNumber: string;
  status: PharmacyPurchaseOrderStatus;
  notes?: string | null;
  expectedDeliveryDate?: string | null;
  sentAt?: string | null;
  verifiedAt?: string | null;
  rejectionReason?: string | null;
  createdAt: string;
  vendor?: { id: string; name: string; phone: string; email?: string | null };
  createdBy?: { id: string; name: string };
  verifiedBy?: { id: string; name: string } | null;
  lines: PurchaseOrderLine[];
  delivery?: {
    id: string;
    invoiceNumber: string;
    invoiceDate: string;
    totalAmount: number;
    fileUrl?: string | null;
    notes?: string | null;
    submittedAt?: string | null;
    lines: VendorDeliveryLine[];
  } | null;
}

export const pharmacyPurchaseApi = {
  list: (status?: PharmacyPurchaseOrderStatus) =>
    api.get(`${base}/purchase-orders`, { params: status ? { status } : undefined }),
  get: (orderId: string) => api.get(`${base}/purchase-orders/${orderId}`),
  create: (body: {
    vendorId: string;
    notes?: string;
    expectedDeliveryDate?: string;
    lines: Array<{ drugId: string; orderedQty: number; notes?: string }>;
  }) => api.post(`${base}/purchase-orders`, body),
  update: (
    orderId: string,
    body: {
      notes?: string;
      expectedDeliveryDate?: string;
      lines?: Array<{ drugId: string; orderedQty: number; notes?: string }>;
    }
  ) => api.patch(`${base}/purchase-orders/${orderId}`, body),
  send: (orderId: string) => api.post(`${base}/purchase-orders/${orderId}/send`),
  cancel: (orderId: string) => api.post(`${base}/purchase-orders/${orderId}/cancel`),
  accept: (
    orderId: string,
    body?: {
      lineIds?: string[];
      deliveryEdits?: {
        invoiceNumber?: string;
        invoiceDate?: string;
        totalAmount?: number | null;
        notes?: string | null;
      };
      lineCorrections?: Array<{
        id: string;
        batchNo: string;
        expiryDate: string;
        quantity: number;
        mrp: number;
        purchaseRate: number;
        gstRate?: number | null;
        discountRate?: number | null;
        notes?: string | null;
      }>;
    }
  ) => api.post(`${base}/purchase-orders/${orderId}/accept`, body ?? {}),
  reject: (orderId: string, reason?: string) =>
    api.post(`${base}/purchase-orders/${orderId}/reject`, { reason }),
  setupVendorPortal: (
    vendorId: string,
    body: { email: string; password: string; name?: string }
  ) => api.post(`${base}/vendors/${vendorId}/portal-access`, body),
};
